const STYLE_ID = 'ttoria-control-centre-select-styles-v1';
const CONTROLLERS = new WeakMap();
const PROXIES = new WeakMap();
let nextId = 0;

const STYLES = `
.ttoria-control-centre .cc-select { position: relative; display: block; min-width: 0; }
.ttoria-control-centre .cc-select__native,
.ttoria-control-centre .cc-select__accessible-label {
  position: absolute !important; width: 1px !important; height: 1px !important;
  min-width: 0 !important; min-height: 0 !important; padding: 0 !important; margin: -1px !important;
  overflow: hidden !important; clip-path: inset(50%) !important; white-space: nowrap !important;
  border: 0 !important;
}
.ttoria-control-centre .cc-select__trigger {
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  width: 100%; min-height: 42px; padding: 9px 11px; border: 1px solid var(--cc-line);
  border-radius: 10px; color: var(--cc-text); background: var(--cc-surface);
  font: 500 13px/1.5 var(--cc-font); text-align: left; cursor: pointer;
  transition: border-color 120ms ease, background-color 120ms ease, box-shadow 120ms ease;
}
.ttoria-control-centre .cc-select__trigger:hover:not(:disabled) {
  border-color: var(--cc-line-strong); background: var(--cc-surface-strong);
}
.ttoria-control-centre .cc-select__trigger:focus-visible,
.ttoria-control-centre .cc-select__trigger[aria-expanded="true"] {
  outline: none; border-color: var(--cc-blue); box-shadow: 0 0 0 3px var(--cc-blue-soft);
}
.ttoria-control-centre .cc-select__trigger:disabled { cursor: not-allowed; opacity: .55; }
.ttoria-control-centre .cc-select__trigger[aria-invalid="true"] { border-color: var(--cc-red); }
.ttoria-control-centre .cc-select__value { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ttoria-control-centre .cc-select__chevron { flex: 0 0 auto; width: 16px; height: 16px; color: var(--cc-muted); }
.ttoria-control-centre .cc-select__trigger[aria-expanded="true"] .cc-select__chevron { transform: rotate(180deg); }
.ttoria-control-centre .cc-select__popup {
  position: fixed; z-index: 16000; box-sizing: border-box; min-width: 0; padding: 5px;
  border: 1px solid var(--cc-line-strong); border-radius: 12px;
  color: var(--cc-text); background: var(--cc-surface); box-shadow: var(--cc-modal-shadow);
}
.ttoria-control-centre .cc-select__options {
  overflow: auto; overscroll-behavior: contain; scrollbar-gutter: stable; outline: none;
  max-height: inherit; border-radius: 8px;
}
.ttoria-control-centre .cc-select__option {
  position: relative; display: flex; align-items: center; gap: 10px; min-height: 38px;
  box-sizing: border-box; margin: 1px 0; padding: 8px 11px 8px 32px; border-radius: 7px;
  color: var(--cc-text); font: 500 13px/1.5 var(--cc-font); cursor: pointer; overflow-wrap: anywhere;
}
.ttoria-control-centre .cc-select__option[aria-selected="true"] { color: var(--cc-blue); font-weight: 750; }
.ttoria-control-centre .cc-select__option[aria-selected="true"]::before {
  content: ''; position: absolute; left: 13px; top: 50%; width: 5px; height: 9px;
  border: solid currentColor; border-width: 0 2px 2px 0; transform: translateY(-65%) rotate(45deg);
}
.ttoria-control-centre .cc-select__option[data-active="true"] { background: var(--cc-blue-soft); }
.ttoria-control-centre .cc-select__option[aria-disabled="true"] { color: var(--cc-subtle); opacity: .6; cursor: not-allowed; }
.ttoria-control-centre .cc-select__group-label {
  padding: 9px 11px 4px; color: var(--cc-muted); font: 750 11px/1.5 var(--cc-font);
}
@media (prefers-reduced-motion: reduce) {
  .ttoria-control-centre .cc-select__trigger { transition: none; }
}
`;

function installStyles(doc) {
  if (doc.getElementById(STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = STYLE_ID;
  style.textContent = STYLES;
  doc.head.append(style);
}

function focusElement(element, options = { preventScroll: true }) {
  if (!element?.isConnected || element.matches(':disabled') || element.closest('[inert]')) return false;
  try { element.focus(options); } catch { element.focus(); }
  return element.ownerDocument.activeElement === element;
}

/** Retarget existing form-validation and render focus selectors to their visible control. */
export function focusControlCentreSelect(element, options = { preventScroll: true }) {
  return focusElement(PROXIES.get(element)?.trigger || element, options);
}

function isUnavailable(option) {
  return option.disabled || option.hidden || (option.parentElement?.tagName === 'OPTGROUP'
    && (option.parentElement.disabled || option.parentElement.hidden));
}

function fieldKey(select) {
  for (const attribute of ['data-testid', 'data-cc-guide-field', 'data-cc-field', 'id', 'name']) {
    if (select.getAttribute(attribute)) return [attribute, select.getAttribute(attribute)];
  }
  return null;
}

function labelText(select) {
  return Array.from(select.labels || []).map(label => {
    const copy = label.cloneNode(true);
    copy.querySelectorAll('select, input, textarea, button, .cc-select').forEach(node => node.remove());
    return copy.textContent.replace(/\s+/g, ' ').trim();
  }).filter(Boolean).join(' ');
}

/** Enhance single-choice selects only inside this Control Centre instance. */
export function installControlCentreSelects(root) {
  if (CONTROLLERS.has(root)) return CONTROLLERS.get(root);
  const doc = root.ownerDocument;
  const win = doc.defaultView;
  installStyles(doc);
  const records = new Map();
  let opened = null;
  let destroyed = false;
  let focusedRecord = null;
  let positionFrame = 0;
  let typeahead = '';
  let typeaheadAt = 0;
  let invalidFocusClaimed = false;

  function enabledIndices(record) {
    return Array.from(record.select.options).flatMap((option, index) => isUnavailable(option) ? [] : [index]);
  }

  function close({ restoreFocus = false } = {}) {
    if (!opened) return;
    const record = opened;
    opened = null;
    record.popup?.remove();
    record.popup = null;
    record.options = null;
    record.trigger.setAttribute('aria-expanded', 'false');
    record.trigger.removeAttribute('aria-activedescendant');
    record.trigger.removeAttribute('aria-controls');
    typeahead = '';
    typeaheadAt = 0;
    if (restoreFocus) focusElement(record.trigger);
  }

  function position() {
    positionFrame = 0;
    if (!opened) return;
    const { trigger, popup, options } = opened;
    if (!trigger.isConnected || trigger.closest('[inert]') || trigger.matches(':disabled')) { close(); return; }
    const rect = trigger.getBoundingClientRect();
    const viewport = win.visualViewport;
    const leftEdge = (viewport?.offsetLeft || 0) + 8;
    const topEdge = (viewport?.offsetTop || 0) + 8;
    const width = viewport?.width || doc.documentElement.clientWidth;
    const height = viewport?.height || win.innerHeight;
    const rightEdge = leftEdge + width - 16;
    const bottomEdge = topEdge + height - 16;
    if (rect.bottom < topEdge || rect.top > bottomEdge || rect.right < leftEdge || rect.left > rightEdge) { close(); return; }
    const popupWidth = Math.min(Math.max(rect.width, 180), Math.max(0, rightEdge - leftEdge));
    popup.style.width = `${popupWidth}px`;
    popup.style.left = `${Math.max(leftEdge, Math.min(rect.left, rightEdge - popupWidth))}px`;
    const below = Math.max(0, bottomEdge - rect.bottom - 6);
    const above = Math.max(0, rect.top - topEdge - 6);
    const useAbove = below < Math.min(options.scrollHeight + 12, 240) && above > below;
    const available = Math.max(0, Math.min(320, (useAbove ? above : below) - 12));
    options.style.maxHeight = `${available}px`;
    const popupHeight = Math.min(options.scrollHeight, available) + 12;
    popup.style.top = `${useAbove ? Math.max(topEdge, rect.top - popupHeight - 6) : rect.bottom + 6}px`;
  }

  function schedulePosition(event) {
    if (!opened || (event?.target instanceof win.Node && opened.options?.contains(event.target))) return;
    if (!positionFrame) positionFrame = win.requestAnimationFrame(position);
  }

  function setActive(record, index, { scroll = true } = {}) {
    if (!record.options || isUnavailable(record.select.options[index] || { disabled: true })) return;
    record.activeIndex = index;
    for (const node of record.options.querySelectorAll('[data-cc-select-index]')) {
      node.dataset.active = String(Number(node.dataset.ccSelectIndex) === index);
    }
    const item = record.options.querySelector(`[data-cc-select-index="${index}"]`);
    if (!item) return;
    record.trigger.setAttribute('aria-activedescendant', item.id);
    if (scroll) {
      const itemRect = item.getBoundingClientRect();
      const listRect = record.options.getBoundingClientRect();
      if (itemRect.top < listRect.top) record.options.scrollTop -= listRect.top - itemRect.top;
      else if (itemRect.bottom > listRect.bottom) record.options.scrollTop += itemRect.bottom - listRect.bottom;
    }
  }

  function populate(record) {
    const list = record.options;
    list.replaceChildren();
    let group = null;
    let groupNode = list;
    Array.from(record.select.options).forEach((option, index) => {
      const parentGroup = option.parentElement?.tagName === 'OPTGROUP' ? option.parentElement : null;
      if (option.hidden || parentGroup?.hidden) return;
      if (parentGroup !== group) {
        group = parentGroup;
        groupNode = list;
        if (group) {
          groupNode = doc.createElement('div');
          groupNode.setAttribute('role', 'group');
          groupNode.setAttribute('aria-label', group.label);
          const heading = doc.createElement('div');
          heading.className = 'cc-select__group-label';
          heading.setAttribute('aria-hidden', 'true');
          heading.textContent = group.label;
          groupNode.append(heading);
          list.append(groupNode);
        }
      }
      const item = doc.createElement('div');
      item.id = `${record.id}-option-${index}`;
      item.className = 'cc-select__option';
      item.dataset.ccSelectIndex = String(index);
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', String(index === record.select.selectedIndex));
      if (isUnavailable(option)) item.setAttribute('aria-disabled', 'true');
      item.textContent = option.label;
      groupNode.append(item);
    });
  }

  function sync(record) {
    const { select, trigger, value, accessibleLabel, validationHint } = record;
    record.host.hidden = select.hidden;
    select.setAttribute('aria-hidden', 'true');
    select.tabIndex = -1;
    value.textContent = select.options[select.selectedIndex]?.label || '';
    trigger.disabled = select.matches(':disabled');
    trigger.setAttribute('aria-required', String(select.required));
    for (const attribute of ['aria-invalid', 'aria-describedby', 'aria-errormessage', 'title']) {
      const attributeValue = select.getAttribute(attribute);
      if (attributeValue !== null) trigger.setAttribute(attribute, attributeValue);
      else trigger.removeAttribute(attribute);
    }
    validationHint.textContent = '';
    if (record.invalid && !select.validity.valid) {
      trigger.setAttribute('aria-invalid', 'true');
      validationHint.textContent = select.validationMessage;
      trigger.setAttribute('aria-describedby', [select.getAttribute('aria-describedby'), validationHint.id].filter(Boolean).join(' '));
    } else if (select.validity.valid) record.invalid = false;
    const labelledBy = select.getAttribute('aria-labelledby');
    const explicitLabel = select.getAttribute('aria-label');
    trigger.removeAttribute('aria-label');
    trigger.removeAttribute('aria-labelledby');
    if (labelledBy) trigger.setAttribute('aria-labelledby', `${labelledBy} ${value.id}`);
    else if (explicitLabel) {
      accessibleLabel.textContent = explicitLabel;
      trigger.setAttribute('aria-labelledby', `${accessibleLabel.id} ${value.id}`);
    } else {
      accessibleLabel.textContent = labelText(select);
      trigger.setAttribute('aria-labelledby', accessibleLabel.textContent ? `${accessibleLabel.id} ${value.id}` : value.id);
    }
    if (opened === record) {
      if (trigger.disabled || select.closest('[inert]')) { close(); return; }
      populate(record);
      const enabled = enabledIndices(record);
      position();
      setActive(record, enabled.includes(record.activeIndex) ? record.activeIndex : enabled[0]);
    }
  }

  function open(record, edge = '') {
    sync(record);
    if (record.trigger.disabled || record.select.closest('[inert]')) return;
    close();
    const popup = doc.createElement('div');
    popup.className = 'cc-select__popup';
    const options = doc.createElement('div');
    options.id = `${record.id}-listbox`;
    options.className = 'cc-select__options';
    options.setAttribute('role', 'listbox');
    options.setAttribute('aria-label', record.accessibleLabel.textContent || labelText(record.select) || 'Options');
    popup.append(options);
    root.append(popup);
    record.popup = popup;
    record.options = options;
    opened = record;
    populate(record);
    record.trigger.setAttribute('aria-expanded', 'true');
    record.trigger.setAttribute('aria-controls', options.id);
    focusElement(record.trigger);
    const enabled = enabledIndices(record);
    const selected = enabled.includes(record.select.selectedIndex) ? record.select.selectedIndex : enabled[0];
    position();
    setActive(record, edge === 'first' ? enabled[0] : edge === 'last' ? enabled.at(-1) : selected);
  }

  function commit(record, index) {
    if (record.select.matches(':disabled') || !record.select.options[index] || isUnavailable(record.select.options[index])) return;
    const changed = record.select.selectedIndex !== index;
    record.select.selectedIndex = index;
    record.invalid = false;
    sync(record);
    close({ restoreFocus: true });
    if (!changed) return;
    try {
      record.select.dispatchEvent(new win.Event('input', { bubbles: true }));
      record.select.dispatchEvent(new win.Event('change', { bubbles: true }));
    } finally {
      // A synchronous form rerender can replace both the select and its trigger.
      refresh();
    }
  }

  function onKeyDown(event) {
    const record = [...records.values()].find(item => item.trigger === event.target)
      || (opened?.popup?.contains(event.target) ? opened : null);
    if (!record || event.isComposing || event.ctrlKey || event.metaKey) return;
    const key = event.key;
    if (key === 'Tab') { if (opened === record) close(); return; }
    if (key === 'Escape') {
      if (opened !== record) return;
      event.preventDefault();
      event.stopPropagation();
      close({ restoreFocus: true });
      return;
    }
    const searchingSpace = key === ' ' && typeahead && win.performance.now() - typeaheadAt <= 700;
    if (!searchingSpace && ['Enter', ' ', 'ArrowDown', 'ArrowUp', 'Home', 'End', 'PageDown', 'PageUp'].includes(key)) {
      event.preventDefault();
      event.stopPropagation();
      if (opened !== record) {
        open(record, key === 'Home' ? 'first' : key === 'End' ? 'last' : '');
        return;
      }
      if (key === 'Enter' || key === ' ') { commit(record, record.activeIndex); return; }
      if (event.altKey && key === 'ArrowUp') { close({ restoreFocus: true }); return; }
      const enabled = enabledIndices(record);
      const current = enabled.indexOf(record.activeIndex);
      const step = key === 'PageDown' ? 8 : key === 'PageUp' ? -8 : key === 'ArrowDown' ? 1 : -1;
      const index = key === 'Home' ? 0 : key === 'End' ? enabled.length - 1 : Math.max(0, Math.min(enabled.length - 1, current + step));
      setActive(record, enabled[index]);
      return;
    }
    if (key.length !== 1 || event.altKey) return;
    event.preventDefault();
    event.stopPropagation();
    if (opened !== record) open(record);
    const now = win.performance.now();
    typeahead = now - typeaheadAt > 700 ? key : typeahead + key;
    typeaheadAt = now;
    const text = typeahead.toLocaleLowerCase();
    const query = [...text].every(character => character === text[0]) ? text[0] : text;
    const indices = enabledIndices(record);
    const start = indices.indexOf(record.activeIndex);
    const ordered = query.length === 1
      ? [...indices.slice(start + 1), ...indices.slice(0, start + 1)]
      : [...indices.slice(Math.max(0, start)), ...indices.slice(0, Math.max(0, start))];
    const match = ordered.find(index => record.select.options[index].label.trim().toLocaleLowerCase().startsWith(query));
    if (match !== undefined) setActive(record, match);
  }

  function onClick(event) {
    const trigger = event.target.closest?.('.cc-select__trigger');
    const record = trigger ? [...records.values()].find(item => item.trigger === trigger) : null;
    if (record) {
      event.preventDefault();
      event.stopPropagation();
      if (opened === record) close({ restoreFocus: true });
      else open(record);
      return;
    }
    const option = event.target.closest?.('[data-cc-select-index]');
    if (opened && option && opened.options.contains(option)) {
      event.preventDefault();
      event.stopPropagation();
      commit(opened, Number(option.dataset.ccSelectIndex));
      return;
    }
    const label = event.target.closest?.('label');
    if (label && !event.target.closest?.('button, input, textarea, select, a[href]')) {
      const labelled = [...records.values()].find(item => Array.from(item.select.labels || []).includes(label));
      if (labelled) { event.preventDefault(); focusElement(labelled.trigger); }
    }
  }

  function onPointerDown(event) {
    if (!opened) return;
    if (opened.popup.contains(event.target)) {
      if (event.target.closest?.('[data-cc-select-index]')) event.preventDefault();
      return;
    }
    if (!opened.host.contains(event.target)) close();
  }

  function onPointerMove(event) {
    if (!opened) return;
    const item = event.target.closest?.('[data-cc-select-index]');
    if (item && opened.options.contains(item)) setActive(opened, Number(item.dataset.ccSelectIndex), { scroll: false });
  }

  function onFocusIn(event) {
    if (opened && event.target !== opened.trigger && !opened.popup.contains(event.target)) close();
    const nativeRecord = records.get(event.target);
    if (nativeRecord) { focusElement(nativeRecord.trigger); return; }
    focusedRecord = [...records.values()].find(record => record.trigger === event.target) || null;
  }

  function enhance(select) {
    const id = `cc-select-${++nextId}`;
    const host = doc.createElement('span');
    host.className = 'cc-select';
    const accessibleLabel = doc.createElement('span');
    accessibleLabel.className = 'cc-select__accessible-label';
    accessibleLabel.id = `${id}-label`;
    const validationHint = doc.createElement('span');
    validationHint.className = 'cc-select__accessible-label';
    validationHint.id = `${id}-validation`;
    validationHint.setAttribute('aria-live', 'polite');
    const trigger = doc.createElement('button');
    trigger.type = 'button';
    trigger.id = `${id}-trigger`;
    trigger.className = 'cc-select__trigger';
    trigger.setAttribute('role', 'combobox');
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    const value = doc.createElement('span');
    value.id = `${id}-value`;
    value.className = 'cc-select__value';
    const chevron = doc.createElementNS('http://www.w3.org/2000/svg', 'svg');
    chevron.setAttribute('viewBox', '0 0 20 20');
    chevron.setAttribute('class', 'cc-select__chevron');
    chevron.setAttribute('aria-hidden', 'true');
    const path = doc.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'm5 7.5 5 5 5-5');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.7');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    chevron.append(path);
    trigger.append(value, chevron);
    const record = { id, select, host, trigger, value, accessibleLabel, validationHint, key: fieldKey(select),
      originalTabIndex: select.getAttribute('tabindex'), originalAriaHidden: select.getAttribute('aria-hidden'),
      popup: null, options: null, activeIndex: select.selectedIndex, invalid: false };
    host.append(accessibleLabel, trigger, validationHint);
    select.after(host);
    select.classList.add('cc-select__native');
    select.setAttribute('aria-hidden', 'true');
    select.tabIndex = -1;
    const onChange = () => sync(record);
    const onInvalid = event => {
      event.preventDefault();
      record.invalid = true;
      sync(record);
      if (!invalidFocusClaimed) {
        invalidFocusClaimed = true;
        focusElement(trigger);
        win.queueMicrotask(() => { invalidFocusClaimed = false; });
      }
    };
    select.addEventListener('input', onChange);
    select.addEventListener('change', onChange);
    select.addEventListener('invalid', onInvalid);
    const observer = new win.MutationObserver(() => sync(record));
    observer.observe(select, { childList: true, subtree: true, characterData: true, attributes: true,
      attributeFilter: ['disabled', 'required', 'selected', 'label', 'value', 'hidden', 'aria-label', 'aria-labelledby', 'aria-invalid', 'aria-describedby', 'aria-errormessage', 'title'] });
    record.cleanup = () => {
      observer.disconnect();
      select.removeEventListener('input', onChange);
      select.removeEventListener('change', onChange);
      select.removeEventListener('invalid', onInvalid);
      select.classList.remove('cc-select__native');
      if (record.originalTabIndex === null) select.removeAttribute('tabindex');
      else select.setAttribute('tabindex', record.originalTabIndex);
      if (record.originalAriaHidden === null) select.removeAttribute('aria-hidden');
      else select.setAttribute('aria-hidden', record.originalAriaHidden);
      host.remove();
      PROXIES.delete(select);
    };
    records.set(select, record);
    PROXIES.set(select, record);
    sync(record);
  }

  function refresh() {
    if (destroyed) return;
    const active = doc.activeElement;
    const restoreKey = focusedRecord && !focusedRecord.trigger.isConnected
      && (!active || active === doc.body || active === doc.documentElement)
      ? focusedRecord.key : null;
    for (const [select, record] of records) {
      if (!root.contains(select) || !root.contains(record.host) || select.multiple || select.size > 1) {
        if (opened === record) close();
        record.cleanup();
        records.delete(select);
      }
    }
    for (const select of root.querySelectorAll('select:not([multiple])')) {
      if (select.size > 1 || select.hidden || select.getAttribute('aria-hidden') === 'true' && !records.has(select)) continue;
      if (!records.has(select)) enhance(select);
      else sync(records.get(select));
    }
    if (restoreKey) {
      const [attribute, value] = restoreKey;
      const replacement = [...records.values()].find(record => record.select.getAttribute(attribute) === value);
      if (replacement) focusElement(replacement.trigger);
    }
  }

  function onReset(event) {
    if (!root.contains(event.target)) return;
    win.queueMicrotask(() => { for (const record of records.values()) if (record.select.form === event.target) { record.invalid = false; sync(record); } });
  }

  root.addEventListener('keydown', onKeyDown, true);
  root.addEventListener('click', onClick, true);
  root.addEventListener('pointermove', onPointerMove);
  doc.addEventListener('focusin', onFocusIn);
  root.addEventListener('reset', onReset);
  doc.addEventListener('pointerdown', onPointerDown, true);
  doc.addEventListener('scroll', schedulePosition, true);
  win.addEventListener('resize', schedulePosition);
  win.visualViewport?.addEventListener('resize', schedulePosition);
  win.visualViewport?.addEventListener('scroll', schedulePosition);

  const controller = {
    refresh,
    destroy() {
      if (destroyed) return;
      destroyed = true;
      close();
      if (positionFrame) win.cancelAnimationFrame(positionFrame);
      for (const record of records.values()) record.cleanup();
      records.clear();
      root.removeEventListener('keydown', onKeyDown, true);
      root.removeEventListener('click', onClick, true);
      root.removeEventListener('pointermove', onPointerMove);
      doc.removeEventListener('focusin', onFocusIn);
      root.removeEventListener('reset', onReset);
      doc.removeEventListener('pointerdown', onPointerDown, true);
      doc.removeEventListener('scroll', schedulePosition, true);
      win.removeEventListener('resize', schedulePosition);
      win.visualViewport?.removeEventListener('resize', schedulePosition);
      win.visualViewport?.removeEventListener('scroll', schedulePosition);
      CONTROLLERS.delete(root);
    },
  };
  CONTROLLERS.set(root, controller);
  refresh();
  return controller;
}
